<template>
  <v-dialog
    :value="value"
    fullscreen
    persistent
    no-click-animation
    transition="dialog-bottom-transition"
    @input="onDialogInput"
  >
    <v-card class="d-flex flex-column ai-builder-card">
      <div class="d-flex align-center px-4 primary white--text ai-builder-toolbar">
        <v-icon color="white" class="mr-2">mdi-creation</v-icon>
        <div class="text-subtitle-1 font-weight-medium">
          {{ $vuetify.lang.t('$vuetify.SITE_BUILDER.AI_BUILDER.DIALOG_TITLE') }}
        </div>
        <v-spacer />
        <v-btn icon :aria-label="$vuetify.lang.t('$vuetify.SITE_BUILDER.AI_BUILDER.CLOSE')" @click="close">
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>
      </div>
      <div class="ai-builder-frame-wrap">
        <v-progress-linear v-if="value && !ready" indeterminate color="primary" absolute top />
        <div v-if="handshakeFailed" class="ai-builder-handshake-error px-4 py-3">
          {{ $vuetify.lang.t('$vuetify.SITE_BUILDER.AI_BUILDER.HANDSHAKE_FAILED') }}
        </div>
        <iframe
          v-if="value"
          ref="frame"
          :name="iframeName"
          :src="embedUrl"
          :title="$vuetify.lang.t('$vuetify.SITE_BUILDER.AI_BUILDER.DIALOG_TITLE')"
          class="ai-builder-frame"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          allow="clipboard-read; clipboard-write"
          @load="onFrameLoad"
        />
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  AI_BUILDER_IFRAME_NAME,
  AI_BUILDER_MESSAGE,
  AI_BUILDER_PROTOCOL_VERSION,
  readApplyMessage,
} from '@/modules/SiteBuilder/aiBuilder/protocol'

const HANDSHAKE_TIMEOUT_MS = 8000

export default {
  props: {
    value: {type: Boolean, default: false},
    pageKey: {type: String, default: ''},
  },
  data() {
    return {
      iframeName: AI_BUILDER_IFRAME_NAME,
      ready: false,
      handshakeFailed: false,
      handshakeTimer: null,
    }
  },
  computed: {
    embedUrl() {
      const base = process.env.VUE_APP_AI_GEN_UI_URL || 'http://localhost:3001'
      return base.replace(/\/+$/, '') + '/?embed=site-builder'
    },
    clientOrigin() {
      return new URL(this.embedUrl).origin
    },
  },
  watch: {
    value() {
      this.ready = false
      this.handshakeFailed = false
      this.clearHandshakeTimer()
    },
  },
  mounted() {
    window.addEventListener('message', this.onWindowMessage)
  },
  beforeDestroy() {
    window.removeEventListener('message', this.onWindowMessage)
    this.clearHandshakeTimer()
  },
  methods: {
    onDialogInput(next) {
      this.$emit('input', next)
    },
    close() {
      this.$emit('input', false)
    },
    clearHandshakeTimer() {
      if (this.handshakeTimer) {
        clearTimeout(this.handshakeTimer)
        this.handshakeTimer = null
      }
    },
    postToFrame(message) {
      const frame = this.$refs.frame
      if (!frame || !frame.contentWindow) return
      frame.contentWindow.postMessage(message, this.clientOrigin)
    },
    onFrameLoad() {
      this.ready = false
      this.handshakeFailed = false
      this.clearHandshakeTimer()
      this.postToFrame({
        type: AI_BUILDER_MESSAGE.HELLO,
        payload: {
          protocolVersion: AI_BUILDER_PROTOCOL_VERSION,
          pageKey: this.pageKey,
          canInsert: this.pageKey !== 'common' && Boolean(this.pageKey),
        },
      })
      this.handshakeTimer = setTimeout(() => {
        if (this.value && !this.ready) this.handshakeFailed = true
      }, HANDSHAKE_TIMEOUT_MS)
    },
    onWindowMessage(event) {
      if (!this.value) return
      const frame = this.$refs.frame
      if (!frame || event.source !== frame.contentWindow) return
      if (event.origin !== this.clientOrigin) return
      const data = event.data
      if (!data || typeof data !== 'object') return
      if (data.type === AI_BUILDER_MESSAGE.READY && data.payload && data.payload.protocolVersion === AI_BUILDER_PROTOCOL_VERSION) {
        this.ready = true
        this.handshakeFailed = false
        this.clearHandshakeTimer()
        return
      }
      const apply = readApplyMessage(data)
      if (apply.ok) {
        this.$emit('apply', apply.sourceHtml)
        return
      }
      if (data.type === AI_BUILDER_MESSAGE.APPLY) {
        this.notifyRejected('invalid-payload')
      }
    },
    notifyApplied(sectionId) {
      this.postToFrame({
        type: AI_BUILDER_MESSAGE.APPLIED,
        payload: {protocolVersion: AI_BUILDER_PROTOCOL_VERSION, sectionId: sectionId},
      })
    },
    notifyRejected(reason) {
      this.postToFrame({
        type: AI_BUILDER_MESSAGE.APPLY_REJECTED,
        payload: {protocolVersion: AI_BUILDER_PROTOCOL_VERSION, reason: reason},
      })
    },
  },
}
</script>

<style scoped>
.ai-builder-card {
  height: 100vh;
}
.ai-builder-toolbar {
  height: 56px;
  flex: 0 0 56px;
}
.ai-builder-frame-wrap {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
}
.ai-builder-frame {
  border: 0;
  width: 100%;
  height: 100%;
  display: block;
}
.ai-builder-handshake-error {
  position: absolute;
  z-index: 2;
  left: 16px;
  right: 16px;
  top: 16px;
  background: #fff4f4;
  color: #8a1f1f;
  border: 1px solid #f0c2c2;
  border-radius: 8px;
}
</style>
